import { useQueryClient } from "@tanstack/react-query";
import { trpc } from "../trpc";
import { getQueryKey } from "@trpc/react-query";

import { Zap } from "./useSearchZaps";
import { OrganizationZaps } from "./useQueryOrganizationZaps";
import { SearchZaps } from "./useQuerySearchZaps";

export function useMoveZapsToFolder() {
  const queryClient = useQueryClient();
  const utils = trpc.useUtils();

  const storageKey = getQueryKey(trpc.zap.storageZaps, undefined, "any");
  const orgKey = getQueryKey(trpc.zap.organizationZaps, undefined, "any");
  const searchKey = getQueryKey(trpc.zap.searchZaps, undefined, "any");

  const mutation = trpc.zap.moveZaps.useMutation({
    // Callback to handle optimistic updates in RQ cache
    onMutate: ({ zapIds, folderId }) => {
      // Update Zap under searchZaps key (search results view)
      const searchKey = getQueryKey(trpc.zap.searchZaps, undefined, "any");
      queryClient.cancelQueries({ queryKey: searchKey }); // cancel outgoing refetches
      queryClient.setQueriesData(
        {
          queryKey: searchKey,
          type: "active",
        },
        (old: SearchZaps | undefined) => {
          // Convert zapIds array to a Set for faster lookups
          const zapIdsSet = new Set(zapIds);
          // Update folderId for each Zap in the cache results array
          if (old?.results?.some((zap) => zapIdsSet.has(String(zap.id)))) {
            return {
              ...old,
              results: old.results.map((zap) => {
                if (zapIdsSet.has(String(zap.id))) {
                  return {
                    ...zap,
                    folderId,
                  };
                }
                return zap;
              }),
            };
          }

          return old;
        },
      );

      // Update Zap under storageZaps key (search results view)
      queryClient.cancelQueries({ queryKey: storageKey });
      queryClient.setQueriesData(
        {
          queryKey: storageKey,
          type: "active",
        },
        (old: Record<string, Zap> | undefined) => {
          const oldHashToBeUpdated = { ...old };

          // Update folderId for each Zap in the cache hash
          for (const id of zapIds) {
            if (oldHashToBeUpdated?.[id]) {
              oldHashToBeUpdated[id] = { ...oldHashToBeUpdated[id], folderId };
            }
          }

          return oldHashToBeUpdated;
        },
      );

      // Update Zap under organizationZaps key (single folder view)
      queryClient.cancelQueries({ queryKey: orgKey });
      queryClient.setQueriesData(
        {
          queryKey: orgKey,
          type: "active",
        },
        (old: OrganizationZaps | undefined) => {
          // Convert zapIds array to a Set for faster lookups
          const zapIdsSet = new Set(zapIds);

          // Update folderId for each Zap in the cache results array
          if (old?.results?.some((zap) => zapIdsSet.has(String(zap.id)))) {
            return {
              ...old,
              // if the zap is in the zapIdsSet, remove it from the results array
              results: old.results.filter((zap) => {
                return !zapIdsSet.has(String(zap.id));
              }),
            };
          }

          return old;
        },
      );
    },
    // Callback to handle cache invalidation after mutation is completed
    onSettled: () => {
      // Invalidate storageZaps key to refetch data
      queryClient.invalidateQueries({ queryKey: storageKey });
      // Invalidate organizationZaps key to refetch data
      queryClient.invalidateQueries({ queryKey: orgKey });
      // Invalidate search Zaps to get most up to date last_modified data
      queryClient.invalidateQueries({ queryKey: searchKey });
      // The unified Folder page reads this query, not organizationZaps.
      utils.asset.getHydratedFolderAssets.invalidate();
    },
  });

  return {
    moveZapsToFolder: mutation.mutate,
    isLoading: mutation.isPending,
  };
}
